import * as React from 'react';
import {
  Grid,
  Box,
  TextField,
  CardHeader,
  Divider,
  CardContent,
  Card,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import type { ILabelValuePair } from '../../../marking/MarkingInterfaces';
import type {
  IWorkViewDetail, IWorkViewPrimaryGroup, IEmployeeInfo, IWorkViewSet,
} from '../WorkViewInterfaces';
import { emptyWorkViewDetail } from '../../../policyConfigurator/shared/Utilities/PolicyConfiguratorHelpers';
import getWorkViewLayouts from '../../../../lib/SWRfetchers/getWorkViewLayouts';
import PrimaryGroupUpsert from './PrimaryGroupUpsert';
import GetWorkViewSetDisplayStyles from '../../../../lib/SWRfetchers/getWorkViewSetDisplayStyles';
import getBinNames from '../../../../lib/SWRfetchers/getBinNames';

const WorkViewUpsert: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workViewIdParam = searchParams.get('workViewId');
  const [WorkViewFormValues, setWorkViewFormValues] = React.useState<IWorkViewDetail>(emptyWorkViewDetail);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const { layouts } = getWorkViewLayouts();
  const { displayStyles } = GetWorkViewSetDisplayStyles();
  const { binNames } = getBinNames();
  const location = useLocation();

  const employeeFetcher = (url: string) => axios.get<{ employees: Array<IEmployeeInfo> }>(url)
    .then((response) => response.data.employees)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching the employees: ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });
  const { data: employees } = useSWR(['/employeeInfo/GetEmployees'], (url) => employeeFetcher(url));

  const handleWorkViewColorChange = (primaryGroupIndex: number, workViewSetIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const tempPrimaryGroups = WorkViewFormValues.primaryGroups;
    tempPrimaryGroups[primaryGroupIndex].sets[workViewSetIndex].color = event.target.value === 'true';
    setWorkViewFormValues({ ...WorkViewFormValues, primaryGroups: tempPrimaryGroups });
  };

  function checkFieldsNotEmpty(obj: IWorkViewDetail): boolean {
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const element = obj[key];

      if (obj.layoutStyle.label === '') {
        enqueueSnackbar(`Cannot save the Work View Information.
        Work View Layout Style not selected. Please select at least option.`,
        { variant: 'error' });
        return false;
      }
      if (key.toLowerCase().includes('id')) {
        // skip
      } else if (element === null || element === undefined || (typeof element === 'string' && element.trim() === '')) {
        enqueueSnackbar(`Cannot save the Work View Information. Work View ${key.charAt(0).toUpperCase() + key.slice(1)} is blank or not valid.`, { variant: 'error' });
        return false;
      }
      if (Array.isArray(element)) {
        if (key === 'users') {
          if (element.length === 0) {
            enqueueSnackbar(`Cannot save the Work View Information.
            Work View ${key.charAt(0).toUpperCase() + key.slice(1)} not selected. Please select at least option.`,
            { variant: 'error' });
            return false;
          }
        } else if (key === 'primaryGroups') {
          for (let j = 0; j < element.length; j += 1) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const primaryGroup: IWorkViewPrimaryGroup = element[j];
            if (!primaryGroup.name) {
              enqueueSnackbar('Cannot save the Work View Information. Primary Group Name is blank or Empty Sets found.', { variant: 'error' });
              return false;
            }
            for (let k = 0; k < primaryGroup.sets.length; k += 1) {
              const workViewSet: IWorkViewSet = primaryGroup.sets[k];
              if (!workViewSet.name || workViewSet.displayStyle.label === '' || workViewSet.bins.length === 0 || workViewSet.name.length > 50) {
                enqueueSnackbar(`Cannot save the Work View Information. Invalid or blank data found on Set #${k + 1}. Please correct it.`, { variant: 'error' });
                return false;
              }
            }
          }
        }
      }
    }
    return true;
  }

  const getWorkViewInfo = () => axios.get<IWorkViewDetail>('/workView/GetWorkViewDetail', {
    headers: { 'Content-Type': 'application/json' },
    params: {
      workViewId: workViewIdParam,
    },
  })
    .then((result) => {
      if (result?.data) {
        setWorkViewFormValues(result.data);
      }
      return result?.data;
    })
    .catch((error: AxiosError) => {
      if (error?.message) {
        enqueueSnackbar('Unable to find Work View.', { persist: false, variant: 'error' });
      }
    });

  const UpsertWorkView = () => {
    console.log(WorkViewFormValues);
    if (checkFieldsNotEmpty(WorkViewFormValues)) {
      setIsFetching(true);
      axios.post<IWorkViewDetail>('/workView/UpsertWorkView', {
        workViewId: WorkViewFormValues.workViewId ? WorkViewFormValues.workViewId : '00000000-0000-0000-0000-000000000000',
        name: WorkViewFormValues.name,
        description: WorkViewFormValues.description,
        users: WorkViewFormValues?.users,
        layoutStyle: WorkViewFormValues.layoutStyle,
        primaryGroups: WorkViewFormValues.primaryGroups,
      })
        .then(() => {
          if (editMode) {
            enqueueSnackbar(`Updated Work View: ${WorkViewFormValues.name}.`, { variant: 'success' });
            getWorkViewInfo().catch(() => {});
          } else {
            enqueueSnackbar(`Added Work View: ${WorkViewFormValues.name}.`, { variant: 'success' });
          }
          navigate('/configureWorkViews');
        })
        .catch((error: AxiosError) => {
          if (error?.message) {
            enqueueSnackbar(`Unable to save Work View information: ${error?.message}`, { persist: false, variant: 'error' });
          }
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  };

  const addNewWorkViewSet = (primaryGroupIndex: number, workViewSetIndex: number) => {
    const tempPrimaryGroup = [...WorkViewFormValues.primaryGroups];
    const tempWorkViewSets = [...WorkViewFormValues.primaryGroups[primaryGroupIndex].sets];
    tempWorkViewSets.splice(workViewSetIndex + 1, 0,
      {
        workViewSetId: '00000000-0000-0000-0000-000000000000', name: '', displayStyle: { label: 'Standard Square', value: 1 }, color: true, bins: [],
      });
    tempPrimaryGroup[primaryGroupIndex].sets = tempWorkViewSets;
    setWorkViewFormValues({ ...WorkViewFormValues, primaryGroups: tempPrimaryGroup });
  };

  const removeWorkViewSet = (primaryGroupIndex: number, workViewSetIndex: number) => {
    const totalSets = WorkViewFormValues.primaryGroups[primaryGroupIndex].sets.length;
    if (totalSets === 1 && workViewSetIndex === 0) {
      enqueueSnackbar('Cannot delete work set. A primary group will require at least 1 work set..', { persist: false, variant: 'error' });
    } else {
      const tempPrimaryGroups = [...WorkViewFormValues.primaryGroups];
      tempPrimaryGroups[primaryGroupIndex].sets.splice(workViewSetIndex, 1);
      setWorkViewFormValues({ ...WorkViewFormValues, primaryGroups: tempPrimaryGroups });
    }
  };

  const duplicateWorkViewSet = (primaryGroupIndex: number, workViewSetIndex: number) => {
    const copyFromValues: IWorkViewSet = WorkViewFormValues.primaryGroups[primaryGroupIndex].sets[workViewSetIndex];
    const tempPrimaryGroup = [...WorkViewFormValues.primaryGroups];
    const tempWorkViewSets = [...WorkViewFormValues.primaryGroups[primaryGroupIndex].sets];
    tempWorkViewSets.splice(workViewSetIndex + 1, 0,
      {
        workViewSetId: '00000000-0000-0000-0000-000000000000', name: copyFromValues.name, displayStyle: copyFromValues.displayStyle, color: copyFromValues.color, bins: copyFromValues.bins,
      });
    tempPrimaryGroup[primaryGroupIndex].sets = tempWorkViewSets;
    setWorkViewFormValues({ ...WorkViewFormValues, primaryGroups: tempPrimaryGroup });
  };

  const handleAddNewPrimaryGroup = () => {
    const tempPrimaryGroups = [...WorkViewFormValues.primaryGroups];
    const workViewSet = {
      workViewSetId: '00000000-0000-0000-0000-000000000000',
      name: '',
      displayStyle: { label: 'Standard Square', value: 1 },
      color: true,
      bins: [],
    };
    tempPrimaryGroups.push({
      primaryGroupId: '00000000-0000-0000-0000-000000000000',
      name: '',
      sets: [workViewSet],
    });
    setWorkViewFormValues({ ...WorkViewFormValues, primaryGroups: tempPrimaryGroups });
  };

  if (location.pathname.includes('create-WorkView')) {
    if (WorkViewFormValues.primaryGroups.length === 0) {
      handleAddNewPrimaryGroup();
    }
  }

  React.useEffect(() => {
    if (workViewIdParam) {
      setEditMode(true);
      getWorkViewInfo().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, workViewIdParam]);

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={1} />
        <Grid container item xs={8} spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">
              {'Build a Work View for Managers to view active work.'}
            </Alert>
          </Grid>
          <br />
          <Grid item xs={12}>
            <Grid xs={12} style={{ backgroundColor: 'white' }}>
              <Card>
                <CardHeader title="General Information" />
                <Divider variant="fullWidth" />
                <CardContent>
                  <Grid item xs={4}>
                    <Box p={2}>
                      <TextField
                        value={WorkViewFormValues?.name}
                        id="workViewName-value-input"
                        label="Name"
                        variant="outlined"
                        type="string"
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        onChange={(event) => {
                          const inputVal = event.target.value;
                          if (inputVal.length >= 50) {
                            enqueueSnackbar('Work View Name cannot be more than 50 characters long.', { persist: false, variant: 'error' });
                          }
                          const tempWorkViewFormValues: IWorkViewDetail = { ...WorkViewFormValues, name: inputVal };
                          setWorkViewFormValues(tempWorkViewFormValues);
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box p={2}>
                      <TextField
                        value={WorkViewFormValues?.description}
                        id="workViewDescription-value-input"
                        label="Description"
                        variant="outlined"
                        type="string"
                        fullWidth
                        onChange={(event) => {
                          const inputVal = event.target.value;
                          const tempWorkViewFormValues: IWorkViewDetail = { ...WorkViewFormValues, description: inputVal };
                          setWorkViewFormValues(tempWorkViewFormValues);
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box p={2}>
                      {
                        (
                          employees && (
                            <Autocomplete
                              key={JSON.stringify(WorkViewFormValues.users)}
                              defaultValue={WorkViewFormValues.users}
                              multiple
                              onChange={(event, selectedUsers: IEmployeeInfo[]) => {
                                const tempWorkViewSetValues = WorkViewFormValues;
                                tempWorkViewSetValues.users = selectedUsers;
                                setWorkViewFormValues(tempWorkViewSetValues);
                              }}
                              autoHighlight
                              autoSelect
                              options={employees.sort((a, b) => -b.name.localeCompare(a.name))}
                              getOptionLabel={(option) => option.name}
                              // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              renderInput={(params) => <TextField {...params} label="Search and Add Users" id="add-users-autocomplete" variant="outlined" />}
                            />
                          )
                        )
                      }
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box p={2}>
                      {
                        layouts && (
                          <Autocomplete
                            value={WorkViewFormValues.layoutStyle}
                            onChange={(event: unknown, selectedLayout: ILabelValuePair | null) => {
                              if (selectedLayout) {
                                const tempFormValues: IWorkViewDetail = { ...WorkViewFormValues, layoutStyle: { label: selectedLayout.label, value: selectedLayout.value } };
                                setWorkViewFormValues?.(tempFormValues);
                              } else {
                                const tempFormValues: IWorkViewDetail = { ...WorkViewFormValues, layoutStyle: { label: '', value: '' } };
                                setWorkViewFormValues?.(tempFormValues);
                              }
                            }}
                            options={layouts.sort((a, b) => -b.label.localeCompare(a.label))}
                            autoHighlight
                            autoSelect
                            getOptionLabel={(option) => option.label.toString()}
                            // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            renderInput={(params) => <TextField {...params} label="Layout Style" id="select-workviewlayout-autocomplete" variant="outlined" />}
                          />
                        )
                      }
                    </Box>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item>
              <Box p={2} />
            </Grid>
            <PrimaryGroupUpsert
              setWorkViewFormValues={setWorkViewFormValues}
              WorkViewFormValues={WorkViewFormValues}
              DisplayStyles={displayStyles}
              bins={binNames}
              isFetching={isFetching}
              workViewIdParam={workViewIdParam}
              handleUpsertWorkView={UpsertWorkView}
              addNewWorkViewSet={addNewWorkViewSet}
              handleAddNewPrimaryGroup={handleAddNewPrimaryGroup}
              handleWorkViewColorChange={handleWorkViewColorChange}
              handleWorkVeiwSetRemove={removeWorkViewSet}
              handleDuplicateWorkViewSet={duplicateWorkViewSet}
            />
          </Grid>
        </Grid>
      </Grid>
      <br />
      <br />
      <br />
      <br />
    </>
  );
};

export default WorkViewUpsert;
